/**
 * useMarketplaceRequestCount — live count for the `MarketplaceSidebar` /
 * `MarketplaceMobileNav` "Requests" badge.
 *
 * The count is the row count of the pending-requests list
 * (`GET /api/marketplace/pro/requests`), read from the shared
 * `stores/marketplaceRequestList.ts` via `useMarketplaceRequestListState`: the
 * sidebar, the phone strip, the header and Home all share one in-flight request
 * and one cached result, and `bumpMarketplaceRequests` (a confirm/decline
 * elsewhere in the app) refetches it once, so the badge never goes stale until a
 * reload and never disagrees with the list. A failed fetch degrades to 0 (no
 * badge) — this is a secondary nav affordance, not the requests page itself.
 * `enabled=false` issues no request.
 */
import { IS_PREVIEW, previewQueryParam } from "../lib/pro-mode";
import { useMarketplaceRequestListState } from "./useMarketplaceRequestList";

/** Mirrors `MarketplaceRequestsPage`'s own `?requests=empty` preview seam so
 *  Playwright/preview screenshots of the sidebar badge are deterministic. */
function previewCount(): number {
  return previewQueryParam("requests") === "empty" ? 0 : 1;
}

export function useMarketplaceRequestCount(enabled = true): number {
  // Preview never reads the network; its count keeps the query-param seam.
  const { rows } = useMarketplaceRequestListState(enabled && !IS_PREVIEW);
  if (IS_PREVIEW) return enabled ? previewCount() : 0;
  return enabled ? (rows?.length ?? 0) : 0;
}
